import { FC } from 'react';
import { useStore } from '@tanstack/react-form';
import type { AnyFieldApi } from '@tanstack/react-form';
import { ButtonInputs, ButtonInputsOption } from '@new/common/ui/ButtonInputs/ButtonInputs';
import { SectionHeading } from '@new/common/ui/SectionHeading/SectionHeading';
import { LabeledNumberInput } from '@new/common/ui/LabeledNumberInput/LabeledNumberInput';
import { LabeledTextarea } from '@new/common/ui/LabeledTextarea/LabeledTextarea';
import { ErrorMessage } from '@new/common/ui/ErrorMessage/ErrorMessage';
import { withForm } from '@new/common/hooks/form';

import { reviewDefaultValues, type ReviewDecision } from './reviewSchema';
import type { EditableCourseRow } from './courseRows';

const DECISION_OPTIONS: ButtonInputsOption<ReviewDecision>[] = [
  { label: 'Approve', value: 'approve' },
  { label: 'Reject', value: 'reject' },
];

const useFieldErrors = (field: AnyFieldApi): string[] =>
  useStore(field.store, (state) =>
    state.meta.isTouched ? state.meta.errors.map((e) => String(e)) : []
  );

interface ApprovedCreditsFieldProps {
  field: AnyFieldApi;
  displayValue: number | null;
  onChange: (value: number | null) => void;
}

const ApprovedCreditsField: FC<ApprovedCreditsFieldProps> = ({ field, displayValue, onChange }) => {
  const errors = useFieldErrors(field);

  return (
    <div className="flex flex-col gap-1">
      <LabeledNumberInput
        label="Approved credits"
        value={displayValue ?? ''}
        min={0}
        max={90}
        hasError={errors.length > 0}
        onChange={onChange}
      />
      <ErrorMessage errors={errors} />
    </div>
  );
};

interface ReviewNoteFieldProps {
  field: AnyFieldApi;
}

const ReviewNoteField: FC<ReviewNoteFieldProps> = ({ field }) => {
  const errors = useFieldErrors(field);

  return (
    <div className="flex flex-col gap-1">
      <LabeledTextarea
        label="Review note"
        placeholder="Explain how many credits are approved and why"
        rows={3}
        value={field.state.value ?? ''}
        hasError={errors.length > 0}
        onChange={(v) => field.handleChange(v)}
      />
      <ErrorMessage errors={errors} />
    </div>
  );
};

export const FacultyReviewSection = withForm({
  defaultValues: reviewDefaultValues,
  props: {
    requestedCredits: 0,
    rows: [] as EditableCourseRow[],
    sumCreditHours: (_rows: EditableCourseRow[]) => 0,
    hasManualCreditOverride: false,
    onManualCreditOverride: () => {},
  },
  render: function FacultyReviewSectionRender({
    form,
    requestedCredits,
    rows,
    sumCreditHours,
    hasManualCreditOverride,
    onManualCreditOverride,
  }) {
    return (
      <section className="flex shrink-0 flex-col gap-4 rounded-lg border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) p-4">
        <SectionHeading title="Faculty review" />

        <div className="grid grid-cols-2 gap-4">
          <LabeledNumberInput label="Requested credits" value={requestedCredits} disabled />
          <form.AppField name="officialCredits">
            {(field) => (
              <ApprovedCreditsField
                field={field}
                displayValue={hasManualCreditOverride ? field.state.value : sumCreditHours(rows)}
                onChange={(v) => {
                  field.handleChange(v);
                  onManualCreditOverride();
                }}
              />
            )}
          </form.AppField>
        </div>

        <form.AppField name="reviewNote">
          {(field) => <ReviewNoteField field={field} />}
        </form.AppField>

        <div className="flex flex-col gap-2">
          <span className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
            Decision
          </span>
          <form.AppField name="decision">
            {(field) => (
              <ButtonInputs<ReviewDecision>
                size="input"
                value={field.state.value}
                onChange={(v) => {
                  field.handleChange(v);
                  if (v === 'reject') onManualCreditOverride();
                }}
                options={DECISION_OPTIONS}
              />
            )}
          </form.AppField>
        </div>
      </section>
    );
  },
});
